import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BadgeCheckIcon, BriefcaseIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, FuelIcon, CircleParkingIcon, RouteIcon, SettingsIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react';
import { fleet, formatNpr, vehicleTypeMeta, type Vehicle, type VehicleType } from '../data/fleet';
import type { TripSearch } from '../types/search';
type CategoryFilter = 'All' | 'SUV' | 'Sedan';
type TypeFilter = 'All' | VehicleType;
interface FleetSectionProps {
  tripSearch: TripSearch;
}
const categoryFilters: CategoryFilter[] = ['All', 'SUV', 'Sedan'];
const typeFilters: TypeFilter[] = ['All', 'Car', 'Jeep', 'Hiace', 'Bus'];
export function FleetSection({
  tripSearch
}: FleetSectionProps) {
  const [category, setCategory] = useState<CategoryFilter>('All');
  const [type, setType] = useState<TypeFilter>('All');
  const [selectedCar, setSelectedCar] = useState<Vehicle | null>(null);
  const visibleCars = fleet.filter(vehicle => (category === 'All' || vehicle.category === category) && (type === 'All' || vehicle.type === type));
  const serviceLabel = tripSearch.service === 'airport' ? 'Airport transfer' : tripSearch.service === 'citytour' ? 'City tour' : 'One-way trip';
  const isCityTour = tripSearch.service === 'citytour';
  return <section id="fleet" className="w-full scroll-mt-16 bg-slate-50 py-16 sm:py-20" data-id="element-72" data-mp="1481yv3v70102">
      <div className="mx-auto max-w-6xl px-4 sm:px-6" data-id="element-73" data-mp="gl3j68ewcx37">
        <div className="rounded-2xl bg-ink px-5 py-5 text-white sm:px-6" data-id="element-74" data-mp="blzd3qn9rfh3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-75" data-mp="bjmoiun0a6v2">
            <div data-id="element-76" data-mp="qc6ojmeuzy8a">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-300" data-id="element-77" data-mp="ljwzs9phd47l">Available cars for your trip</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xl font-bold sm:text-2xl" data-id="element-78" data-mp="3tyf1zusqppja">
                {isCityTour ? <span data-id="element-79" data-mp="3ozucoqf10q9m">{tripSearch.pickup || 'Your city'} city tour</span> : <><span data-id="element-80" data-mp="1sxqxhiko52iw">{tripSearch.pickup || 'Your pickup'}</span><RouteIcon className="h-5 w-5 text-accent" aria-hidden="true" data-id="element-81" data-mp="1swz145j8z0rk" /><span data-id="element-82" data-mp="1xvinu85o0gkz">{tripSearch.drop || 'Your destination'}</span></>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-semibold" data-id="element-83" data-mp="22o3hn9okmtoi">
              <span className="rounded-full bg-white/10 px-3 py-1.5" data-id="element-84" data-mp="26odun6as6kcv">{serviceLabel}</span>
              <span className="rounded-full bg-white/10 px-3 py-1.5 capitalize" data-id="element-85" data-mp="19ymr6hkihmux">{isCityTour ? `${tripSearch.cityTourDuration === 'half-day' ? 'Half day' : 'Full day'} tour` : `${tripSearch.shift} travel`}</span>
              {tripSearch.date && <span className="rounded-full bg-white/10 px-3 py-1.5" data-id="element-86" data-mp="1iy01tdkdghh8">{tripSearch.date}{tripSearch.time ? ` · ${tripSearch.time}` : ''}</span>}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-87" data-mp="2uezsy5uzgq9o">
          <div data-id="element-88" data-mp="2zcrtfo9p1kph">
            <h2 className="font-display text-3xl font-800 text-ink" data-id="element-89" data-mp="2zc9tohntooct">Select a car</h2>
            <p className="mt-2 text-sm text-ink-soft" data-id="element-90" data-mp="39hhpjdif1uls">One clear price for your route — driver, fuel, parking and listed coverage included.</p>
          </div>
          <div className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-ink shadow-sm ring-1 ring-slate-200" data-id="element-91" data-mp="34khjv3phatv7">{visibleCars.length} cars available</div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6" data-id="element-92" data-mp="2vpbkamgsx814">
          <FilterRow label="Category" data-id="element-93" data-mp="2qrjin0qcu7jq">
            {categoryFilters.map(item => <Chip key={item} active={category === item} onClick={() => setCategory(item)} data-id="element-94" data-mp="2lr44w825uy18">{item}</Chip>)}
          </FilterRow>
          <FilterRow label="Type" data-id="element-95" data-mp="2gtc6qwaxafol">
            {typeFilters.map(item => <Chip key={item} active={type === item} onClick={() => setType(item)} data-id="element-96" data-mp="2cr8u44cip51q">{item}</Chip>)}
          </FilterRow>
        </div>

        <AnimatePresence data-id="element-97" data-mp="ezwejuz6ssri">
          {selectedCar && <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} className="mt-6 flex flex-col gap-3 rounded-xl border border-brand-200 bg-brand-50 p-4 text-brand-900 sm:flex-row sm:items-center sm:justify-between" role="status" data-id="element-98" data-mp="azvo1bytcr43">
              <div className="flex items-center gap-3" data-id="element-99" data-mp="91unqqzxwf3h">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500 text-white" data-id="element-100" data-mp="d2xzb1ayfoyw"><CheckIcon className="h-5 w-5" data-id="element-101" data-mp="3vhfb8mmde6p1" /></span>
                <p className="text-sm font-semibold" data-id="element-102" data-mp="3vfm3t53xnifz"><span className="font-bold" data-id="element-103" data-mp="43jd4e1tm0k7">{selectedCar.name}</span> selected for {isCityTour ? `${tripSearch.pickup} city tour` : `${tripSearch.pickup} to ${tripSearch.drop}`}.</p>
              </div>
              <a href="/book" className="rounded-lg bg-brand-600 px-4 py-2 text-center text-sm font-bold text-white hover:bg-brand-700" data-id="element-104" data-mp="3qi2qc7f279p3">Continue booking</a>
            </motion.div>}
        </AnimatePresence>

        <div className="mt-8 grid grid-cols-1 gap-5" data-id="element-105" data-mp="3hgw73ql0zks5">
          <AnimatePresence mode="popLayout" data-id="element-106" data-mp="2xw4bt98c88gb">
            {visibleCars.map(vehicle => <VehicleResultCard key={vehicle.id} vehicle={vehicle} shift={tripSearch.shift} selected={selectedCar?.id === vehicle.id} onSelect={() => setSelectedCar(vehicle)} data-id="element-107" data-mp="2xxyxwqvci6lz" />)}
          </AnimatePresence>
        </div>

        {visibleCars.length === 0 && <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-id="element-108" data-mp="3gtf9ipebmttn">
            <p className="font-display text-lg font-700 text-ink" data-id="element-109" data-mp="3bvn7h2jyprbq">No cars match those filters</p>
            <button type="button" onClick={() => {
          setCategory('All');
          setType('All');
        }} className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white" data-id="element-110" data-mp="3qk0667afpo9x">Reset filters</button>
          </div>}
      </div>
    </section>;
}
function VehicleResultCard({
  vehicle,
  shift,
  selected,
  onSelect
}: {
  vehicle: Vehicle;
  shift: TripSearch['shift'];
  selected: boolean;
  onSelect: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const rate = shift === 'day' ? vehicle.dayRate : vehicle.nightRate;
  const TypeIcon = vehicleTypeMeta[vehicle.type].icon;
  return <motion.article layout initial={{
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 14
  }} className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${selected ? 'border-brand-500 ring-2 ring-brand-100' : 'border-slate-200'}`} data-id="element-111" data-mp="317kdxsjl0apr">
      <div className="flex flex-col sm:flex-row" data-id="element-112" data-mp="3g0nt5t9pacdb">
        <div className="relative h-44 shrink-0 bg-slate-100 sm:h-auto sm:w-5/12" data-id="element-113" data-mp="3kypekkulmnry">
          <img src={vehicle.image} alt={vehicle.name} className="h-full w-full object-cover" data-id="element-114" data-mp="3ozrel3wcb3uz" />
          {vehicle.popular && <span className="absolute left-3 top-3 rounded-full bg-accent px-2.5 py-1 text-xs font-bold text-ink" data-id="element-115" data-mp="1qkogw3asc6tg">Popular</span>}
        </div>

        <div className="flex min-w-0 flex-1 flex-col p-5" data-id="element-116" data-mp="1unb1pk91grh5">
          <div className="flex items-start justify-between gap-3" data-id="element-117" data-mp="1n2qz2l4pr3f8">
            <div data-id="element-118" data-mp="1ib8zo1ic9awd">
              <div className="flex items-center gap-2" data-id="element-119" data-mp="1iaqsway1vea7">
                <TypeIcon className="h-4 w-4 text-brand-500" data-id="element-120" data-mp="1i95yqmfndxcv" />
                <span className="text-xs font-bold uppercase tracking-wide text-brand-700" data-id="element-121" data-mp="1dcpcxyvx7v2y">{vehicle.category} · {vehicle.type}</span>
              </div>
              <h3 className="mt-1 font-display text-xl font-800 text-ink" data-id="element-122" data-mp="260l2mjn2omq8">{vehicle.name}</h3>
            </div>
            <div className="text-right" data-id="element-123" data-mp="211qfj5i2oc0b">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-124" data-mp="21204cprdsd4f">{shift} fare</p>
              <p className="font-display text-xl font-800 text-ink" data-id="element-125" data-mp="1wybyqcmb7rlt">{formatNpr(rate)}</p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5 text-xs font-medium text-ink-soft" data-id="element-126" data-mp="1s34xdbgiiz02">
            <span className="flex items-center gap-1" data-id="element-127" data-mp="lidhvmkw41a1"><UsersIcon className="h-3.5 w-3.5 text-slate-400" data-id="element-128" data-mp="gldks160q8v2" />{vehicle.seats} seats</span>
            <span className="flex items-center gap-1" data-id="element-129" data-mp="2gnd2tvoclu5o"><BriefcaseIcon className="h-3.5 w-3.5 text-slate-400" data-id="element-130" data-mp="2gml96qeba6i1" />{vehicle.luggage} bags</span>
            <span className="flex items-center gap-1" data-id="element-131" data-mp="2kr1b6gkt0trz"><SettingsIcon className="h-3.5 w-3.5 text-slate-400" data-id="element-132" data-mp="26pypi4xq1lhl" />{vehicle.transmission}</span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 border-y border-slate-100 py-3 text-xs font-semibold text-ink-soft" data-id="element-133" data-mp="2bo9y6cca5py8">
            <InclusionItem icon={<BadgeCheckIcon className="h-4 w-4 text-brand-500" data-id="element-135" data-mp="2zew520ajt46n" />} text={vehicle.driverExperience} data-id="element-134" data-mp="2zffhj1hfj0da" />
            <InclusionItem icon={<RouteIcon className="h-4 w-4 text-brand-500" data-id="element-137" data-mp="34fa62ydqfdv6" />} text={vehicle.includedKm} data-id="element-136" data-mp="34cnywzur70dh" />
            <InclusionItem icon={<FuelIcon className="h-4 w-4 text-brand-500" data-id="element-139" data-mp="2unnwf6h1lmuj" />} text="Fuel included" data-id="element-138" data-mp="2pmgqze2dgzr1" />
            <InclusionItem icon={<CircleParkingIcon className="h-4 w-4 text-brand-500" data-id="element-141" data-mp="3idi9np59xdfb" />} text="Parking included" data-id="element-140" data-mp="2ull0k5t7kzhw" />
          </div>

          <div className="mt-4 flex flex-wrap gap-2" data-id="element-142" data-mp="3n4gsavdqinlq">
            <button type="button" onClick={() => setDetailsOpen(value => !value)} aria-expanded={detailsOpen} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-bold text-brand-700 hover:bg-brand-50" data-id="element-143" data-mp="3n59tlgtxckwh">
              {detailsOpen ? 'Hide details' : 'View details'}
              {detailsOpen ? <ChevronUpIcon className="h-4 w-4" data-id="element-144" data-mp="1kje8gkw5mmxp" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-145" data-mp="1gf6h0fybasvs" />}
            </button>
            <button type="button" onClick={onSelect} className={`ml-auto rounded-lg px-4 py-2 text-sm font-bold transition-colors ${selected ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200' : 'bg-brand-500 text-white hover:bg-brand-600'}`} data-id="element-146" data-mp="sq3zd83oh01a">
              {selected ? 'Car selected' : 'Select car'}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-147" data-mp="wq5sutz9en5h">
        {detailsOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden border-t border-slate-100 bg-slate-50" data-id="element-148" data-mp="2d55xcwkpevox">
            <div className="grid gap-4 p-5 sm:grid-cols-2" data-id="element-149" data-mp="10lzjcz2qtw8e">
              <DetailList heading="Trip inclusions" items={vehicle.inclusions} icon={<CheckIcon className="h-4 w-4 text-brand-500" data-id="element-151" data-mp="vneqxltqggui" />} data-id="element-150" data-mp="vmmuk8sbaf36" />
              <DetailList heading="Vehicle highlights" items={vehicle.features} icon={<ShieldCheckIcon className="h-4 w-4 text-brand-500" data-id="element-153" data-mp="mukwui9mncwl" />} data-id="element-152" data-mp="qv4wbshoaslw" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.article>;
}
function InclusionItem({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <span className="flex items-center gap-1.5" data-id="element-154" data-mp="cukcqu3xosrc">{icon}{text}</span>;
}
function DetailList({
  heading,
  items,
  icon
}: {
  heading: string;
  items: string[];
  icon: React.ReactNode;
}) {
  return <div data-id="element-155" data-mp="3ws24395q9aa">
      <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-156" data-mp="3x9xyyrwqlho">{heading}</h4>
      <ul className="mt-2 space-y-1.5" data-id="element-157" data-mp="22ajq6nel54cs">
        {items.map(item => <li key={item} className="flex items-center gap-2 text-sm text-ink-soft" data-id="element-158" data-mp="1y8ydp35yqs34">{icon}{item}</li>)}
      </ul>
    </div>;
}
function FilterRow({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-2" data-id="element-159" data-mp="3l565f2w3brob"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-160" data-mp="3l43fax6ldt9g">{label}</span><div className="flex flex-wrap gap-1.5" data-id="element-161" data-mp="3pz0uklugsyus">{children}</div></div>;
}
function Chip({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${active ? 'bg-ink text-white' : 'bg-white text-ink-soft ring-1 ring-slate-200 hover:bg-slate-100'}`} data-id="element-162" data-mp="2xa3dgm1zp83m">{children}</button>;
}